import FrameMessenger from 'frame-messenger';
import { whenDocumentReady, getData, findElements, addStyle } from 'dom-helper';
import assign from 'object-assign';
import { GWITracker } from 'gwi-tracker';
import SuperwidgetSDK from 'superwidget-sdk';
import Mason from './mason';
import TrailerModal from './trailer-modal';
import Logger from '../utils/logger';
import ENV from '../config/env';
import VERSION from '../config/version';
import BrickConfigClassMap from '../utils/brick_config_class_map';
import BrickModal from '../models/brick-modal';
import FrameEventManager from './frame-event-manager';
import AssetsService from '../services/assets-service';
import isMobile from '../utils/mobile-checker';
import {
  MASON_VARIABLE_KEY_NAME,
  DEFAULT_MASON_VARIABLE_NAME,
} from '../config/constants';

export default class Foreman {
  constructor() {
    this.frameEventManager = new FrameEventManager();
    this.bricks = [];
    this.apiKey = null;
    this.propagateActions = null;
    this.iframe = false;
    this.inWidget = false;
    this.defaultTrackingOptions = {};
  }

  init(_w) {
    if (_w[MASON_VARIABLE_KEY_NAME] === undefined) {
      _w[MASON_VARIABLE_KEY_NAME] = DEFAULT_MASON_VARIABLE_NAME;
    }

    // Do this before calling flushQueue to ensure that
    // BrickLayer.queue is not null.
    Foreman.initBrickLayerSDK(_w);

    this.addFrameHandlers();
    this.flushQueue(_w);
    this.setWindowVariable(_w);

    if (!this.iframe) {
      AssetsService.appendBrickAssets();
    }

    GWITracker.initializeSDK(this.apiKey);

    // There is a race condition where both SDKs include the analytics code.
    // This is a blunt way to fix that issue.
    setTimeout(() =>
      SuperwidgetSDK.initializeSDK(this.apiKey, { superwidgetHost: ENV.superwidgetHost }),
      500,
    );

    whenDocumentReady(() => {
      Mason.initializeHTMLBricks(this.defaultParameters()).then((brickDataArray) => {
        brickDataArray.forEach(
            brickData => this.buildWithBrickConfig(brickData.element, brickData),
        );
      });
    });

    // Call on ready callback.
    if (_w.goWatchItBrickLayerReady && typeof _w.goWatchItBrickLayerReady === 'function') {
      _w.goWatchItBrickLayerReady();
    }
  }

  static initBrickLayerSDK(_w) {
    _w.BrickLayer = _w.BrickLayer || {
      q: [],
      queue: (...args) => {
        (_w.BrickLayer.q = _w.BrickLayer.q || []).push(args);
      },
    };
  }

  flushQueue(_w) {
    Logger.debug('Queue:', _w[_w[MASON_VARIABLE_KEY_NAME]].q);

    (_w[_w[MASON_VARIABLE_KEY_NAME]].q || []).forEach((args) => {
      this.queue(...args);
    });
  }

  setWindowVariable(_w) {
    Logger.debug('Setting variable to', _w[MASON_VARIABLE_KEY_NAME]);
    _w[_w[MASON_VARIABLE_KEY_NAME]] = this.getExportedSDK();
  }

  queue(...args) {
    const sdk = this.getExportedSDK();

    const parameters = [];
    const keys = Object.keys(args);

    keys.forEach((key) => {
      parameters.push(args[key]);
    });

    const action = parameters[0];
    const actionParameters = parameters.slice(1);

    if (sdk[action]) {
      sdk[action](...actionParameters);
    } else {
      Logger.error('Unrecognized action', action);
    }
  }

  configure(setting) {
    Logger.debug('Called configure with config:', setting);

    if (setting.apiKey) {
      Logger.debug('Setting apiKey to', setting.apiKey);
      this.apiKey = setting.apiKey;
    }

    if (setting.propagateActions) {
      Logger.debug('Setting propagateActions to', setting.propagateActions);
      this.propagateActions = setting.propagateActions;
    }

    if (setting.defaultTrackingOptions) {
      Logger.debug('Setting defaultTrackingOptions to', setting.defaultTrackingOptions);
      this.defaultTrackingOptions = setting.defaultTrackingOptions;
    }

    if (setting.iframe !== undefined) {
      Logger.debug('Setting iframe to', setting.iframe);
      this.iframe = setting.iframe;
    }

    if (setting.inWidget !== undefined) {
      Logger.debug('Setting inWidget to', setting.inWidget);
      this.inWidget = setting.inWidget;
    }

    if (setting.debug) {
      ENV.debug = true;
      Logger.debug('Set debug to true.');
    }
  }

  defaultParameters() {
    return {
      inWidget: this.inWidget,
      buildWithIframe: this.iframe,
      apiKey: this.apiKey,
    };
  }

  build(el, params) {
    const data = getData(el);
    const parameters = {
      ...data,
      ...params,
      ...this.defaultParameters(),
    };

    const BrickConfigClass = BrickConfigClassMap.get(parameters.brickType);
    const brickConfig = new BrickConfigClass(parameters);

    this.buildWithBrickConfig(el, brickConfig);
  }

  async buildWithBrickConfig(el, brickConfig) {
    const brick = await Mason.build(el, brickConfig);
    if (brick) {
      this.registerBrick(brick);
    }
  }

  notify(event) {
    if (event === 'sign-in-change') {
      this.notifySignInChange();
    }
  }

  getExportedSDK() {
    if (this.exportedSDK) {
      return this.exportedSDK;
    }

    this.exportedSDK = {
      version: VERSION,
      build: (el, parameters) => this.build(el, parameters),
      on: (eventName, handler) => this.frameEventManager.addEventListener(eventName, handler),
      off: eventName => this.frameEventManager.removeEventListener(eventName),
      configure: object => this.configure(object),
      queue: (...args) => this.queue(...args),
      notify: event => this.notify(event),
    };

    return this.exportedSDK;
  }

  registerBrick(brick) {
    this.bricks.push(brick);
  }

  getBrickWithId(id) {
    return this.bricks.filter(brick => brick.id === id)[0];
  }

  notifySignInChange() {
    if (!this.iframe) {
      FrameMessenger.send(window, 'sign-in-state-changed', {}, '*');
      return;
    }

    this.bricks.forEach((brick) => {
      if (brick.iframe.contentWindow) {
        FrameMessenger.send(brick.iframe.contentWindow, 'sign-in-state-changed', {}, '*');
      } else {
        Logger.warn('Could not refresh iframe with id', brick.id);
      }
    });
  }

  showMobileModal(brickConfig) {
    const brick = BrickModal.showBrick(brickConfig);
    this.registerBrick(brick);
  }

  static findFrameElementByName(name) {
    return findElements(`[name='${name}']`)[0];
  }

  addFrameHandlers() {
    this.frameEventManager.addFrameHandler('trailer-clicked', (frameName, data) => {
      TrailerModal.remove();

      if (this.propagateActions) {
        const newData = data;

        newData.trackingOptions = assign(
            this.defaultTrackingOptions,
            newData.trackingOptions || {},
        );

        FrameMessenger.sendToParent('trailer-clicked', newData);
        return;
      }

      const trailerUrl = data.youtubeUrl;
      const { trackingState, trackingOptions } = data;

      Logger.debug('Opening trailer modal with youtubeUrl:', trailerUrl);

      TrailerModal.showTrailer(trackingState, trailerUrl, trackingOptions);
    });

    this.frameEventManager.addFrameHandler('sign-in-required', () => {
      Logger.debug('Default sign in required event');

      window.open(`${ENV.websiteHost}/sign-in/modal`, '_blank', 'height=500,width=400');
    });

    this.frameEventManager.addFrameHandler('sign-in-change', () => {
      this.notifySignInChange();
    });

    this.frameEventManager.addFrameHandler('fire-tracking-event', (frameName, data) => {
      // This is used for when the brick is in the list widget, overwrite
      // the tracking options to adhere to the list widget's correct tracking
      // parameters, i.e. medium / mediumContext / etc.
      const { event } = data;
      let { parameters } = data;
      parameters = assign(parameters, this.defaultTrackingOptions);

      if (this.propagateActions) {
        FrameMessenger.sendToParent('fire-tracking-event', { event, parameters });
        return;
      }

      GWITracker.fireAnalyticsTransaction(event, parameters);
    });

    this.frameEventManager.addFrameHandler('brick-clicked', (frameName, data) => {
      const selectedBrick = this.getBrickWithId(data.brickId);

      if (!selectedBrick) {
        Logger.warn('Could not find saved brick that matches the id', data.brickId);
        return;
      }

      if (this.propagateActions) {
        const brickConfig = selectedBrick.config;
        brickConfig.defaultTrackingOptions = this.defaultTrackingOptions;

        FrameMessenger.sendToParent('open-mobile-modal', { brickConfig });
        return;
      }

      if (isMobile()) {
        this.showMobileModal(selectedBrick.config);
      }
    });

    this.frameEventManager.addFrameHandler('open-mobile-modal', (frameName, data) => {
      const BrickConfigClass = BrickConfigClassMap.get(data.brickConfig.brickType);
      const brickConfig = new BrickConfigClass(data.brickConfig);

      if (isMobile()) {
        this.showMobileModal(brickConfig);
      }
    });

    this.frameEventManager.addFrameHandler('open-widget', (frameName, data) => {
      const el = Foreman.findFrameElementByName(frameName);
      const flavor = 'availability';

      SuperwidgetSDK.openModal(data.type, data.id, flavor, { animate: el, ...data });
    });

    this.frameEventManager.addFrameHandler('frame-resize', (frameName, data) => {
      const el = Foreman.findFrameElementByName(frameName);

      addStyle(el, { height: `${data.widgetHeight}px` });
    });
  }
}



// WEBPACK FOOTER //
// ./src/models/foreman.js